/**
 * RackCarouselController
 * 
 * Controls automatic switching between racks based on activity queue.
 * Maintains an LRU (Least Recently Used) cache of rack structures
 * to minimize API calls and improve performance.
 * 
 * Features:
 * - Automatic rack switching with configurable dwell time
 * - LRU cache for rack structures (max 10 racks)
 * - Preloading of next rack in background
 * - Smooth transitions with fade in/out
 * - Manual pause/resume capability
 * - Cache hit/miss statistics
 * 
 * @author BizClick WMS
 * @version 1.26 - Fixed rack number type to string
 */

import { RackStructure } from '../rack-builder/types';
import {
  CarouselState,
  CarouselConfig,
  CacheEntry,
  CacheStats,
  TransitionState,
  CarouselOperationResult,
  QueueItem,
  DEFAULT_CAROUSEL_CONFIG
} from './types';

/**
 * Callback type for rack structure loading
 */
export type RackLoader = (
  warehouseId: string,
  rackNumber: string
) => Promise<RackStructure>;

/**
 * Callback type for transition events
 */
export type TransitionCallback = (state: TransitionState) => void;

/**
 * Callback type for state change events
 */
export type StateChangeCallback = (state: CarouselState) => void;

export class RackCarouselController {
  private config: CarouselConfig;
  private state: CarouselState;
  private transitionState: TransitionState;
  private cache: Map<string, CacheEntry> = new Map();
  private cacheStats: CacheStats = {
    size: 0,
    maxSize: 10,
    hits: 0,
    misses: 0,
    hitRate: 0,
    evictions: 0
  };
  private dwellTimer: NodeJS.Timeout | null = null;
  private transitionTimer: NodeJS.Timeout | null = null;
  private rackLoader: RackLoader;
  private onTransition?: TransitionCallback;
  private onStateChange?: StateChangeCallback;

  /**
   * Create a new RackCarouselController
   * @param rackLoader Function to load rack structures
   * @param config Optional configuration
   */
  constructor(
    rackLoader: RackLoader,
    config?: Partial<CarouselConfig>
  ) {
    this.config = { ...DEFAULT_CAROUSEL_CONFIG, ...config };
    this.cacheStats.maxSize = this.config.maxCacheSize;
    this.rackLoader = rackLoader;

    this.state = {
      currentRack: null,
      currentWarehouseId: null,
      isAutoSwitching: this.config.autoSwitch,
      isPaused: false,
      displayedAt: null,
      remainingDwellTime: this.config.dwellTime,
      nextRack: null
    };

    this.transitionState = {
      isTransitioning: false,
      phase: 'idle',
      previousRack: null,
      progress: 0
    };

    console.log('[RackCarouselController] Initialized with config:', this.config);
  }

  /**
   * Set transition callback
   */
  public setTransitionCallback(callback: TransitionCallback): void {
    this.onTransition = callback;
  }

  /**
   * Set state change callback
   */
  public setStateChangeCallback(callback: StateChangeCallback): void {
    this.onStateChange = callback;
  }

  /**
   * Switch to next rack from queue
   * @param queueItem The next rack to display
   * @returns Operation result with loaded rack structure
   */
  public async switchToRack(queueItem: QueueItem): Promise<CarouselOperationResult> {
    try {
      console.log(
        `[RackCarouselController] Switching to rack ${queueItem.rackNumber} ` +
        `with ${queueItem.events.length} events`
      );

      // Cancel any existing timers
      this.cancelTimers();

      // Start transition: Fade out
      if (this.config.smoothTransitions && this.state.currentRack !== null) {
        await this.startTransition('fade-out');
      }

      // Update state
      const previousRack = this.state.currentRack;
      this.state.currentRack = queueItem.rackNumber;
      this.state.currentWarehouseId = queueItem.warehouseId;
      this.state.displayedAt = new Date();
      this.state.remainingDwellTime = this.config.dwellTime;

      // Load rack structure (from cache or API)
      const structure = await this.loadRackStructure(
        queueItem.warehouseId,
        queueItem.rackNumber
      );

      // Transition: Loading complete, fade in
      if (this.config.smoothTransitions) {
        await this.startTransition('fade-in');
      }

      // Notify state change
      this.notifyStateChange();

      // Start dwell timer if auto-switching is enabled
      if (this.state.isAutoSwitching && !this.state.isPaused) {
        this.startDwellTimer();
      }

      return {
        success: true,
        message: `Switched to rack ${queueItem.rackNumber}`,
        state: this.getState(),
        cacheStats: this.getCacheStats()
      };
    } catch (error) {
      console.error('[RackCarouselController] Switch error:', error);
      return {
        success: false,
        message: `Failed to switch: ${error}`
      };
    }
  }

  /**
   * Load rack structure from cache or API
   */
  private async loadRackStructure(
    warehouseId: string,
    rackNumber: string
  ): Promise<RackStructure> {
    const cacheKey = this.getCacheKey(warehouseId, rackNumber);

    // Check cache first
    const cached = this.cache.get(cacheKey);
    if (cached) {
      this.cacheStats.hits++;
      cached.lastAccessedAt = new Date();
      cached.accessCount++;
      this.updateCacheHitRate();

      console.log(
        `[RackCarouselController] Cache HIT for rack ${rackNumber} ` +
        `(access count: ${cached.accessCount})`
      );

      return cached.structure;
    }

    // Cache miss - load from API
    this.cacheStats.misses++;
    this.updateCacheHitRate();

    console.log(`[RackCarouselController] Cache MISS for rack ${rackNumber}, loading...`);

    const structure = await this.rackLoader(warehouseId, rackNumber);

    // Add to cache
    this.addToCache(warehouseId, rackNumber, structure);

    return structure;
  }

  /**
   * Add rack structure to LRU cache
   */
  private addToCache(
    warehouseId: string,
    rackNumber: string,
    structure: RackStructure
  ): void {
    const cacheKey = this.getCacheKey(warehouseId, rackNumber);

    // Check if cache is full
    if (this.cache.size >= this.config.maxCacheSize) {
      this.evictLRU();
    }

    // Add new entry
    const entry: CacheEntry = {
      warehouseId,
      rackNumber,
      structure,
      loadedAt: new Date(),
      lastAccessedAt: new Date(),
      accessCount: 1
    };

    this.cache.set(cacheKey, entry);
    this.cacheStats.size = this.cache.size;

    console.log(
      `[RackCarouselController] Added rack ${rackNumber} to cache ` +
      `(size: ${this.cache.size}/${this.config.maxCacheSize})`
    );
  }

  /**
   * Evict least recently used entry from cache
   */
  private evictLRU(): void {
    let oldestKey: string | null = null;
    let oldestTime = Date.now();

    // Find least recently used entry
    this.cache.forEach((entry, key) => {
      const lastAccess = entry.lastAccessedAt.getTime();
      if (lastAccess < oldestTime) {
        oldestTime = lastAccess;
        oldestKey = key;
      }
    });

    // Remove oldest entry
    if (oldestKey) {
      const evicted = this.cache.get(oldestKey);
      this.cache.delete(oldestKey);
      this.cacheStats.evictions++;
      this.cacheStats.size = this.cache.size;

      if (evicted) {
        console.log(
          `[RackCarouselController] Evicted rack ${evicted.rackNumber} from cache ` +
          `(accessed ${evicted.accessCount} times)`
        );
      }
    }
  }

  /**
   * Preload next rack in background
   */
  public async preloadNextRack(queueItem: QueueItem): Promise<void> {
    if (!this.config.preloadNext) {
      return;
    }

    try {
      this.state.nextRack = queueItem;
      console.log(`[RackCarouselController] Preloading rack ${queueItem.rackNumber}...`);

      await this.loadRackStructure(queueItem.warehouseId, queueItem.rackNumber);

      console.log(`[RackCarouselController] Rack ${queueItem.rackNumber} preloaded`);
    } catch (error) {
      console.error('[RackCarouselController] Preload error:', error);
    }
  }

  /**
   * Start dwell timer for current rack
   */
  private startDwellTimer(): void {
    this.dwellTimer = setTimeout(() => {
      console.log('[RackCarouselController] Dwell time expired, ready for next rack');
      // Notify that dwell time has expired (parent component will handle switching)
      this.notifyStateChange();
    }, this.config.dwellTime);
  }

  /**
   * Start transition animation
   */
  private async startTransition(phase: 'fade-out' | 'fade-in'): Promise<void> {
    return new Promise((resolve) => {
      this.transitionState.isTransitioning = true;
      this.transitionState.phase = phase;
      this.transitionState.progress = 0;

      if (phase === 'fade-out') {
        this.transitionState.previousRack = this.state.currentRack;
      }

      this.notifyTransition();

      // Simulate transition progress
      const steps = 10;
      const stepDuration = this.config.transitionDuration / steps;
      let currentStep = 0;

      const progressInterval = setInterval(() => {
        currentStep++;
        this.transitionState.progress = (currentStep / steps) * 100;
        this.notifyTransition();

        if (currentStep >= steps) {
          clearInterval(progressInterval);
          this.transitionState.isTransitioning = false;
          this.transitionState.phase = 'idle';
          this.transitionState.progress = 0;
          this.notifyTransition();
          resolve();
        }
      }, stepDuration);
    });
  }

  /**
   * Pause auto-switching
   */
  public pause(): void {
    this.state.isPaused = true;
    this.cancelTimers();
    console.log('[RackCarouselController] Paused');
    this.notifyStateChange();
  }

  /**
   * Resume auto-switching
   */
  public resume(): void {
    this.state.isPaused = false;
    if (this.state.isAutoSwitching && this.state.currentRack !== null) {
      this.startDwellTimer();
    }
    console.log('[RackCarouselController] Resumed');
    this.notifyStateChange();
  }

  /**
   * Toggle auto-switching on/off
   */
  public toggleAutoSwitch(): void {
    this.state.isAutoSwitching = !this.state.isAutoSwitching;
    
    if (this.state.isAutoSwitching && !this.state.isPaused && this.state.currentRack !== null) {
      this.startDwellTimer();
    } else {
      this.cancelTimers();
    }

    console.log(`[RackCarouselController] Auto-switch: ${this.state.isAutoSwitching}`);
    this.notifyStateChange();
  }

  /**
   * Clear cache
   */
  public clearCache(): void {
    const clearedCount = this.cache.size;
    this.cache.clear();
    this.cacheStats.size = 0;
    console.log(`[RackCarouselController] Cache cleared (${clearedCount} items removed)`);
  }

  /**
   * Get current state
   */
  public getState(): CarouselState {
    return { ...this.state };
  }

  /**
   * Get transition state
   */
  public getTransitionState(): TransitionState {
    return { ...this.transitionState };
  }

  /**
   * Get cache statistics
   */
  public getCacheStats(): CacheStats {
    return { ...this.cacheStats };
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<CarouselConfig>): void {
    this.config = { ...this.config, ...config };
    
    if (config.maxCacheSize !== undefined) {
      this.cacheStats.maxSize = config.maxCacheSize;
      
      // Evict excess entries if new max size is smaller
      while (this.cache.size > config.maxCacheSize) {
        this.evictLRU();
      }
    }

    console.log('[RackCarouselController] Configuration updated:', this.config);
  }

  /**
   * Get current configuration
   */
  public getConfig(): CarouselConfig {
    return { ...this.config };
  }

  /**
   * Clean up resources
   */
  public destroy(): void {
    this.cancelTimers();
    this.clearCache();
    console.log('[RackCarouselController] Destroyed');
  }

  /**
   * Cancel all active timers
   */
  private cancelTimers(): void {
    if (this.dwellTimer) {
      clearTimeout(this.dwellTimer);
      this.dwellTimer = null;
    }
    if (this.transitionTimer) {
      clearTimeout(this.transitionTimer);
      this.transitionTimer = null;
    }
  }

  /**
   * Generate cache key
   */
  private getCacheKey(warehouseId: string, rackNumber: string): string {
    return `${warehouseId}-${rackNumber}`;
  }

  /**
   * Update cache hit rate
   */
  private updateCacheHitRate(): void {
    const totalRequests = this.cacheStats.hits + this.cacheStats.misses;
    this.cacheStats.hitRate = totalRequests > 0
      ? (this.cacheStats.hits / totalRequests) * 100
      : 0;
  }

  /**
   * Notify transition callback
   */
  private notifyTransition(): void {
    if (this.onTransition) {
      this.onTransition(this.transitionState);
    }
  }

  /**
   * Notify state change callback
   */
  private notifyStateChange(): void {
    if (this.onStateChange) {
      this.onStateChange(this.state);
    }
  }
}

export default RackCarouselController;
